import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { YOUTUBE_SUGGESTED_PRIVACY } from 'fym-shared/src/music24/campaignLaunch';
import type { YouTubePrivacyStatus, YouTubeSettingsDraft } from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { usePatchPromoChannelMutation } from '../../../../redux/api/fymAPI';
import { youtubeMessages } from '../composer/youtube/youtubeMessages';
import { YouTubeSettingsFields } from '../composer/youtube/YouTubeSettingsFields';
import { SerializedSocialChannel } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  title: { id: 'app.sml.promote.channel.postingDefaults.title', defaultMessage: 'Posting defaults' },
  none: {
    id: 'app.sml.promote.channel.postingDefaults.none',
    defaultMessage: 'Not set. We select "Public" when you schedule your next YouTube post.',
  },
  set: { id: 'app.sml.promote.channel.postingDefaults.set', defaultMessage: 'Set' },
  change: { id: 'app.sml.promote.channel.postingDefaults.change', defaultMessage: 'Change' },
  dialogTitle: {
    id: 'app.sml.promote.channel.postingDefaults.dialog.title',
    defaultMessage: 'Posting defaults for YouTube',
  },
  dialogBody: {
    id: 'app.sml.promote.channel.postingDefaults.dialog.body',
    defaultMessage:
      'We select this for your next YouTube posts and campaigns. You can change it each time. We ask "Made for kids" for each campaign.',
  },
  save: { id: 'app.sml.promote.channel.postingDefaults.save', defaultMessage: 'Save' },
  failed: {
    id: 'app.sml.promote.channel.postingDefaults.failed',
    defaultMessage: 'We could not save the posting defaults. Try again.',
  },
});

const PRIVACY_MESSAGES = {
  public: youtubeMessages.privacyPublic,
  unlisted: youtubeMessages.privacyUnlisted,
  private: youtubeMessages.privacyPrivate,
} as const;

interface ChannelPostingDefaultsProps {
  accountId: number;
  channel: SerializedSocialChannel;
}

const noop = () => undefined;

/**
 * "Posting defaults" of the YouTube channel: who can see a video. The launch questions and the
 * Composer show it as the selected value, and the last launch answer replaces it; the artist
 * changes it here. "Made for kids" is not a default: it is asked for each campaign and each post
 * (owner decision 2026-10-11). TikTok has no such section: TikTok wants the user to choose for the
 * posts in front of them.
 */
const ChannelPostingDefaultsComponent: React.FC<ChannelPostingDefaultsProps> = ({ accountId, channel }) => {
  const intl = useIntl();
  const stored: YouTubePrivacyStatus | null = channel.postingDefaults?.youtube?.privacyStatus ?? null;
  // Null: the dialog is closed.
  const [privacy, setPrivacy] = useState<YouTubePrivacyStatus | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const [patchChannel, { isLoading }] = usePatchPromoChannelMutation();

  const open = useCallback(() => {
    setHasFailed(false);
    setPrivacy(stored ?? YOUTUBE_SUGGESTED_PRIVACY);
  }, [stored]);
  const close = useCallback(() => setPrivacy(null), []);
  const draft = useMemo<YouTubeSettingsDraft>(() => ({ privacyStatus: privacy, madeForKids: null }), [privacy]);
  const save = useCallback(() => {
    if (privacy === null) {
      return;
    }
    setHasFailed(false);
    patchChannel({ accountId, platform: channel.platform, postingDefaults: { youtube: { privacyStatus: privacy } } })
      .unwrap()
      .then(close)
      .catch((error: unknown) => {
        console.error(error);
        setHasFailed(true);
      });
  }, [accountId, channel.platform, close, patchChannel, privacy]);

  return (
    <div className={styles.defaults} data-testid="posting-defaults">
      <div className={styles.defaultsText}>
        <span className={styles.defaultsTitle}>{intl.formatMessage(messages.title)}</span>
        <span className={styles.quietNote}>
          {stored === null ? intl.formatMessage(messages.none) : intl.formatMessage(PRIVACY_MESSAGES[stored])}
        </span>
      </div>
      <button type="button" className={styles.defaultsLink} onClick={open}>
        {intl.formatMessage(stored === null ? messages.set : messages.change)}
      </button>
      {privacy === null ? null : (
        <ConfirmDialog
          isOpen
          tone="neutral"
          testId="posting-defaults-dialog"
          title={intl.formatMessage(messages.dialogTitle)}
          description={intl.formatMessage(messages.dialogBody)}
          confirmLabel={intl.formatMessage(messages.save)}
          isBusy={isLoading}
          error={hasFailed ? intl.formatMessage(messages.failed) : undefined}
          onConfirm={save}
          onCancel={close}
        >
          <YouTubeSettingsFields
            draft={draft}
            idPrefix="youtube-defaults"
            onPrivacy={setPrivacy}
            onMadeForKids={noop}
            showMadeForKids={false}
          />
        </ConfirmDialog>
      )}
    </div>
  );
};

export const ChannelPostingDefaults = TypedMemo(ChannelPostingDefaultsComponent);
